// src/components/ui/Card.tsx
import React from "react";
import { DynamicIcon, type IconName } from "lucide-react/dynamic";

// Base card variants
const cardVariants = {
  default: "bg-gray-800/30 rounded-xl",
  elevated: "bg-gray-800/30 rounded-xl shadow-lg",
  bordered: "bg-gray-800/40 rounded-xl border border-gray-700/50",
} as const;

const cardPadding = {
  sm: "p-3",
  md: "p-4 sm:p-5",
  lg: "p-5 sm:p-6",
  none: "",
} as const;

interface CardProps {
  children: React.ReactNode;
  variant?: keyof typeof cardVariants;
  padding?: keyof typeof cardPadding;
  className?: string;
}

export const Card: React.FC<CardProps> = ({
  children,
  variant = "default",
  padding = "md",
  className = "",
}) => (
  <div className={`${cardVariants[variant]} ${cardPadding[padding]} ${className}`}>
    {children}
  </div>
);

// Loading skeleton variants for DataCard
type LoadingVariant = "chart" | "stats" | "table" | "card";

const CardSkeleton: React.FC<{ variant: LoadingVariant; height?: number | undefined }> = ({ variant, height }) => {
  if (variant === "stats") {
    return (
      <div className="animate-pulse">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-6 h-6 rounded-md bg-gray-700/50" />
          <div className="flex-1">
            <div className="h-3 bg-gray-700/50 rounded w-24 mb-1" />
            <div className="h-2 bg-gray-700/30 rounded w-32" />
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="bg-gray-700/20 rounded-lg p-3 space-y-2">
              <div className="h-2 bg-gray-700/40 rounded w-20" />
              <div className="h-6 bg-gray-700/50 rounded w-16" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (variant === "table") {
    return (
      <div className="animate-pulse" style={height ? { minHeight: height } : undefined}>
        <div className="flex items-center gap-2 mb-4">
          <div className="w-6 h-6 rounded-md bg-gray-700/50" />
          <div className="flex-1">
            <div className="h-3 bg-gray-700/50 rounded w-32 mb-1" />
            <div className="h-2 bg-gray-700/30 rounded w-40" />
          </div>
        </div>
        {/* Table header */}
        <div className="flex gap-2 mb-2 pb-2 border-b border-gray-700/30">
          {[...Array(5)].map((_, i) => (
            <div key={i} className={`h-3 bg-gray-700/40 rounded ${i === 0 ? "w-24" : "flex-1"}`} />
          ))}
        </div>
        {/* Table rows */}
        <div className="space-y-2">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="flex gap-2 py-2">
              {[...Array(5)].map((_, j) => (
                <div key={j} className={`h-4 bg-gray-700/30 rounded ${j === 0 ? "w-24" : "flex-1"}`} />
              ))}
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (variant === "card") {
    return (
      <div className="animate-pulse" style={height ? { minHeight: height } : undefined}>
        <div className="flex items-center gap-2 mb-3">
          <div className="w-6 h-6 rounded-md bg-gray-700/50" />
          <div className="flex-1">
            <div className="h-3 bg-gray-700/50 rounded w-32 mb-1" />
            <div className="h-2 bg-gray-700/30 rounded w-40" />
          </div>
        </div>
        <div className="space-y-3">
          <div className="h-4 bg-gray-700/40 rounded w-3/4" />
          <div className="h-4 bg-gray-700/30 rounded w-full" />
          <div className="h-4 bg-gray-700/30 rounded w-5/6" />
        </div>
      </div>
    );
  }

  // Default 'chart' variant
  const chartHeight = height ?? 320;
  return (
    <div className="animate-pulse">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded-md bg-gray-700/50" />
          <div>
            <div className="h-3 bg-gray-700/50 rounded w-32 mb-1" />
            <div className="h-2 bg-gray-700/30 rounded w-40" />
          </div>
        </div>
        <div className="h-8 bg-gray-700/30 rounded-lg w-32" />
      </div>
      <div className="relative w-full bg-gray-700/10 rounded-lg" style={{ height: chartHeight - 80 }} />
    </div>
  );
};

// Card with header (for charts, metrics, etc.)
interface DataCardProps extends Omit<CardProps, 'children'> {
  title?: string | undefined;
  subtitle?: string | undefined;
  icon?: IconName | undefined;
  iconColor?: string | undefined;
  actions?: React.ReactNode;
  height?: number | undefined;
  children?: React.ReactNode | undefined;
  /** Shows skeleton loading state when true */
  isLoading?: boolean | undefined;
  /** Skeleton variant to show when loading (default: 'chart') */
  loadingVariant?: LoadingVariant | undefined;
  /** Accent color for header divider */
  accent?: "green" | "blue" | "amber" | "purple" | "red" | undefined;
}

const accentStyles: Record<string, { iconBg: string; iconText: string; divider: string }> = {
  green: { iconBg: "bg-green-500/15", iconText: "text-green-400", divider: "via-green-400/30" },
  amber: { iconBg: "bg-amber-500/15", iconText: "text-amber-400", divider: "via-amber-400/30" },
  blue: { iconBg: "bg-blue-500/15", iconText: "text-blue-400", divider: "via-blue-400/30" },
  purple: { iconBg: "bg-purple-500/15", iconText: "text-purple-400", divider: "via-purple-400/30" },
  red: { iconBg: "bg-red-500/15", iconText: "text-red-400", divider: "via-red-400/30" },
  default: { iconBg: "bg-slate-500/15", iconText: "text-slate-400", divider: "via-white/10" },
};

export const DataCard: React.FC<DataCardProps> = ({
  title,
  subtitle,
  icon,
  iconColor,
  actions,
  height,
  children,
  variant = "default",
  padding = "md",
  className = "",
  isLoading = false,
  loadingVariant = "chart",
  accent,
}) => {
  const accentStyle = accentStyles[accent ?? "default"] ?? accentStyles["default"];
  const effectiveIconColor = iconColor ?? accentStyle?.iconText ?? "text-slate-400";

  // Check if className contains flex-col to enable flex-grow behavior for children
  const isFlexCol = className.includes("flex-col");

  return (
    <Card variant={variant} padding={padding} className={className}>
      {isLoading ? (
        <CardSkeleton variant={loadingVariant} height={height} />
      ) : (
        <>
          {(title || subtitle || icon || actions) && (
            <>
              <div className="flex items-center justify-between mb-2 flex-shrink-0">
                <div className="flex items-center gap-2 min-w-0">
                  {icon && (
                    <div className={`w-6 h-6 rounded-md ${accentStyle?.iconBg ?? "bg-slate-500/15"} flex items-center justify-center`}>
                      <DynamicIcon name={icon} className={`w-3.5 h-3.5 ${effectiveIconColor}`} />
                    </div>
                  )}
                  <div className="min-w-0">
                    {title && <div className="text-xs uppercase tracking-wide text-slate-300 truncate">{title}</div>}
                    {subtitle && <div className="text-[11px] text-slate-400 truncate">{subtitle}</div>}
                  </div>
                </div>
                {actions && <div className="flex items-center gap-2 flex-shrink-0">{actions}</div>}
              </div>
              {accent && (
                <div className={`h-px mt-2 mb-3 bg-gradient-to-r from-transparent ${accentStyle?.divider ?? "via-white/10"} to-transparent flex-shrink-0`} />
              )}
            </>
          )}
          <div 
            style={height ? { height } : undefined} 
            className={`${height ? "overflow-hidden" : ""} ${isFlexCol ? "flex-1 min-h-0 flex flex-col" : ""}`.trim() || undefined}
          >
            {children}
          </div>
        </>
      )}
    </Card>
  );
};

// Stat card (for KPIs, metric boxes)
type StatVariant = "default" | "positive" | "negative" | "neutral";

interface StatCardProps {
  label: string;
  value: React.ReactNode;
  /** Numeric change value (renders as percentage) */
  change?: number | null | undefined;
  /** Direction for trend indicator */
  trend?: "up" | "down" | undefined;
  /** Custom trend value string (e.g. "5.2%") */
  trendValue?: string | undefined;
  /** Subtitle text below value */
  subtitle?: string | undefined;
  icon?: IconName;
  variant?: StatVariant;
  className?: string;
}

export const StatCard: React.FC<StatCardProps> = ({
  label,
  value,
  change,
  trend,
  trendValue,
  subtitle,
  icon,
  variant = "default",
  className = "",
}) => {
  const variantStyles: Record<StatVariant, string> = {
    default: "text-white",
    positive: "text-green-400",
    negative: "text-red-400",
    neutral: "text-gray-300",
  };

  // Determine trend direction from change if not explicitly provided
  const effectiveTrend = trend ?? (change !== null && change !== undefined ? (change >= 0 ? "up" : "down") : undefined);

  return (
    <Card variant="default" padding="sm" className={className}>
      <div className="flex items-center justify-between">
        <span className="text-xs text-gray-400">{label}</span>
        {icon && <DynamicIcon name={icon} className="w-3.5 h-3.5 text-gray-500" />}
      </div>
      <div className={`text-lg font-semibold ${variantStyles[variant]}`}>
        {value}
      </div>
      {/* Show trend with trendValue or change */}
      {(effectiveTrend || change !== null && change !== undefined) && (
        <div className={`text-xs ${effectiveTrend === "up" ? "text-green-400" : "text-red-400"}`}>
          {effectiveTrend === "up" ? "▲" : "▼"}{" "}
          {trendValue ?? (change !== null && change !== undefined ? `${change >= 0 ? "+" : ""}${change.toFixed(2)}%` : "")}
        </div>
      )}
      {subtitle && (
        <div className="text-xs text-gray-500 mt-1">{subtitle}</div>
      )}
    </Card>
  );
};

export default Card;

